import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from './BusinessContext';

// Define the context type
interface UnreadMessagesContextType {
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  fetchUnreadCount: () => Promise<void>;
}

// Create the context with default values
const UnreadMessagesContext = createContext<UnreadMessagesContextType>({
  unreadCount: 0,
  setUnreadCount: () => {},
  fetchUnreadCount: async () => {},
});

// Create a provider component
export const UnreadMessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { selectedBusiness } = useBusiness();
  
  // Function to fetch the unread message count
  const fetchUnreadCount = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { count, error } = await supabase
        .from('all_channels')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .or('status.eq.unread,is_read.is.false');
      
      if (error) {
        console.error('Error fetching unread message count:', error);
        return;
      }
      
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error in fetchUnreadCount:', error);
    }
  };
  
  // Set up polling and subscription for unread count when business changes
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;
    
    // Fetch immediately on load
    fetchUnreadCount();
    
    // Set up polling every 20 seconds (as a fallback)
    const intervalId = setInterval(fetchUnreadCount, 20000);
    
    // Subscribe to message changes using Supabase's realtime functionality
    const subscription = supabase
      .channel('all_channels_changes')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'all_channels',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('New message inserted:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'all_channels',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('Message updated:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes', 
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'all_channels',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('Message deleted:', payload);
          fetchUnreadCount();
        }
      )
      .subscribe((status) => {
        console.log('UnreadMessages subscription status:', status);
      });
    
    // Clean up on unmount or when business changes
    return () => {
      clearInterval(intervalId);
      if (supabase) {
        supabase.removeChannel(subscription);
      }
    };
  }, [selectedBusiness?.id]);
  
  return (
    <UnreadMessagesContext.Provider value={{ unreadCount, setUnreadCount, fetchUnreadCount }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

// Custom hook to use the unread messages context
export const useUnreadMessages = () => useContext(UnreadMessagesContext); 